import React, { useRef, useState } from 'react';
import Modal from '@tencent/dui/lib/components/Modal';
import Tabs from '@tencent/dui/lib/components/Tabs';
import Button from '@tencent/dui/lib/components/Button';
import i18n from '@i18next/i18n';
import BulletsMenu from '../bullets-menu';
import { ActionType } from '@src/framework/editor/src/js/constants/common-action-type';
import { getDigitalNumeberData, getItemSymbolData } from '@editor/src/js/constants/toolbar-data';
import ColorPanel from '../color-panel';
import BulletsSize from '../bullets-size';
import CustomizeBullets from '../customize-bullets';
import StartNumber from '../start-number';
import style from './style.less';
import { CharacterTypes } from '@src/slide/domain/ui-model/element-modeles/property/text/constants/character-type';
import { BulletProps } from '../interface';
import {
    IColorPickerManager,
} from '@src/slide/application/service/color-picker-manager/color-picker-manager.interface';
import {
    TDWVer5Type,
    TDWVer6Type,
} from '@src/slide/infrastructure/module/dependent/report/offline-data-report/constant';
import { ITDColor } from '@src/slide/domain/ui-model/element-modeles/property/color/td-color';
import { ICopyPasteImageService } from '@src/slide/application/service/copy-paste/copy-paste.interface';
import { IDynamicService } from '@src/slide/infrastructure/module/independent/dynamic-loader/dynamic-service';

interface Props {
    visible: boolean;
    defaultTabId?: string;
    defaultColor?: ITDColor | null;
    defaultSize?: number;
    defaultStartNumber?: number;
    defaultType?: string;
    defaultCustomText?: string;
    defaultCustomImage?: string;
    onClose: () => void;
    onOk: (props: BulletProps) => void;
    colorPickerManager?: IColorPickerManager;
    copyPasteImageService?: IDynamicService<ICopyPasteImageService>;
}

export type IBulletsAndNumberingDialog = React.FC<Props>;
const BulletsAndNumberingDialog: IBulletsAndNumberingDialog = (props: Props) => {
    const {
        visible, defaultTabId, defaultColor, defaultSize,
        defaultStartNumber, defaultType, defaultCustomText,
        defaultCustomImage, onClose, onOk,
        copyPasteImageService,
    } = props;
    const [activeTabId, setActiveTabId] = useState(defaultTabId || ActionType.ITEMNUMBER);
    const [color, setColor] = useState(defaultColor);
    const [size, setSize] = useState(defaultSize);
    const [startNumber, setStartNumber] = useState(defaultStartNumber);
    const [type, setType] = useState(defaultType);
    const [customText, setCustomText] = useState(defaultCustomText);
    const [customImage, setCustomImage] = useState(defaultCustomImage);
    const customImageId = useRef<string>();

    const handleColorChange = (tdColor: ITDColor | null, source) => {
        setColor(tdColor);
        props.colorPickerManager?.colorPickerTdwReport({
            color: tdColor?.srgb_color || undefined,
            colorPickerFrom: TDWVer6Type.COLOR_PICKER_TOOLBAR,
            colorGroupFrom: source,
            actionType: TDWVer5Type.COLOR_PICKER_BULLETS,
        });
    };
    const handleSizeChange = (size: number) => {
        setSize(size);
    };
    const handleStartNumberChange = (startNumber: number) => {
        setStartNumber(startNumber);
    };
    const handleTypeChange = (type: string) => {
        setType(type);
    };
    const handleCustomTextChange = (customText: string) => {
        if (customText) {
            // 如果当前有自定义文字，那么就设置为自定义文字类型
            setType(CharacterTypes.CUSTOM_TEXT);
            setCustomText(customText);
            setCustomImage(undefined);
        } else {
            // 没有文字的话，设置为默认的圆点类型
            setType(CharacterTypes.ITEMSYMBOL_SMALL_CIRCLE);
            setCustomText(undefined);
        }
    };
    const handleCustomImageChange = (customImage: {id: string, src: string}) => {
        setType(CharacterTypes.CUSTOM_IMAGE);
        setCustomImage(customImage.src);
        customImageId.current = customImage.id;
        setCustomText(undefined);
    };
    const handleOk = (event) => {
        // 根据 dui 文档，没有 event 参数表示是 enter 按键触发。
        // 按键触发保存时由于不会触发 input 的 blur，也就无法触发 input 值的合法化校验，导致非法参数被保存，所以在这里禁用 enter 保存
        if (!event) {
            return;
        }
        onOk({
            color,
            size,
            startNumber,
            type,
            customText,
            customImage: customImageId.current,
        });
    };

    const isResetBtnDisabled = () => {
        const isCustomText = customText && type === CharacterTypes.CUSTOM_TEXT;
        const isCustomImage = customImage && type === CharacterTypes.CUSTOM_IMAGE;
        const isBulletTab = activeTabId === ActionType.ITEMNUMBER;
        const enableResetBtn = isBulletTab && (isCustomText || isCustomImage);
        return !enableResetBtn;
    };

    const handleReset = () => {
        if (isResetBtnDisabled()) {
            return;
        }
        setType(CharacterTypes.ITEMSYMBOL_SMALL_CIRCLE);
        if (type === CharacterTypes.CUSTOM_TEXT) {
            setCustomText(undefined);
        }
        if (type === CharacterTypes.CUSTOM_IMAGE) {
            setCustomImage(undefined);
        }
    };

    const actionElement = () => <Button disabled={isResetBtnDisabled()}>{i18n.t('重置')}</Button>;

    // 禁止设置颜色/大小/起始编号
    const disabled = type === CharacterTypes.ITEMSYMBOL_NONE;
    const containerDom = document.querySelector('#workbench') as HTMLDivElement;

    return (
        <Modal
            title={i18n.t('项目符号和编号')}
            visible={visible}
            action={actionElement()}
            onClose={onClose}
            onCancel={onClose}
            onOk={handleOk}
            onClickAction={handleReset}
            className={'bullets-and-numbering-dialog'}
            containerDom={containerDom}
        >
            {<Tabs
                tabBarStyle={{ borderBottom: '1px solid #f1f1f1' }}
                style={{ width: '100%' }}
                activeId={activeTabId}
                onChange={setActiveTabId}
            >
                <Tabs.TabPane tab={i18n.t('项目符号')} id={ActionType.ITEMNUMBER}>
                    <BulletsMenu
                        selectedId={type}
                        onChange={handleTypeChange}
                        // @ts-ignore 目标文件是 js 文件，参数类型推断错误且无法标注类型
                        data={getItemSymbolData(color?.srgb_color, customText, customImage)}
                    ></BulletsMenu>
                </Tabs.TabPane>
                <Tabs.TabPane tab={i18n.t('编号')} id={ActionType.DIGITNUMBER}>
                    <BulletsMenu
                        selectedId={type}
                        onChange={handleTypeChange}
                        // @ts-ignore 目标文件是 js 文件，参数类型推断错误且无法标注类型
                        data={getDigitalNumeberData(color?.srgb_color, startNumber)}
                    ></BulletsMenu>
                </Tabs.TabPane>
            </Tabs>}
            <div className={style['dialog-modal-operation-area']}>
                <ColorPanel
                    disabled={disabled}
                    color={color}
                    onChange={handleColorChange}/>
                <BulletsSize disabled={disabled} size={size} onChange={handleSizeChange}/>
                {
                    activeTabId === ActionType.DIGITNUMBER
                    && <StartNumber disabled={disabled} startNumber={startNumber} onChange={handleStartNumberChange}/>
                }
                {
                    activeTabId === ActionType.ITEMNUMBER
                    && <CustomizeBullets
                        text={customText}
                        onTextChange={handleCustomTextChange}
                        onImageChange={handleCustomImageChange}
                        uploadImageCallback={async (files: Blob[]) => {
                            const imageManager = (await copyPasteImageService?.promise())
                                ?.ImageEditManager.getInstance();
                            // 这里图片压缩好像没生效
                            return await imageManager?.uploadWithoutQuickLook(
                                files,
                                i18n.t('正在上传图片...'),
                                {
                                    maxWidth: 500,
                                    maxHeight: 500,
                                    minWidth: 10,
                                    minHeight: 10,
                                    minSize: 250000,
                                },
                            );
                        }}
                    />
                }
            </div>
        </Modal>
    );
};

export default BulletsAndNumberingDialog;
